import classNames from 'classnames';
import {
  addHours,
  addMinutes,
  differenceInHours,
  isPast,
  parseISO,
} from 'date-fns';
import { formatEther } from 'ethers/lib/utils';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useMemo, VFC } from 'react';
import Countdown, { zeroPad } from 'react-countdown';
import { useToggle } from 'react-use';

import { useCreation } from '@/apis/aniftyBackend/creations/getCreation';
import {
  MintAskStatus,
  useMintAsk,
} from '@/apis/aniftyBackend/mintAsks/getMintAsk';
import { AskHashWithCreation } from '@/apis/aniftyBackend/mintAsks/getMintAsks';
import {
  MintBidWithUser,
  useMintBidsByAskHash,
} from '@/apis/aniftyBackend/mintAsks/getMintBidsByAskHash';
import { MintBidWithCreation } from '@/apis/aniftyBackend/mintBids/getCurrentUserMintBids';
import CreationThumbnail from '@/components/CreationThumbnail';
import CustomButton from '@/components/CustomButton';
import FramedIcon from '@/components/FramedIcon';
import routes from '@/config/routes';
import AuctionStatusIcon from '@/containers/AuctionActivity/AuctionActivityCard/AuctionStatusIcon';
import BidStatus from '@/containers/AuctionActivity/AuctionActivityCard/BidStatus';
import ClaimAuctionModal from '@/containers/AuctionActivity/AuctionActivityCard/ClaimAuctionModal';
import useETHPrice from '@/hooks/useETHPrice';
import useWalletAddress from '@/hooks/useWalletAddress';
import { convertImgixURL } from '@/utils/imgixUtils';

import styles from './AuctionActivityCard.module.scss';

type Props = {
  askHashWithCreation?: AskHashWithCreation;
  mintBidWithCreation?: MintBidWithCreation;
};

const AuctionActivityCard: VFC<Props> = ({
  askHashWithCreation,
  mintBidWithCreation,
}: Props) => {
  const { t, i18n } = useTranslation();
  const creation =
    askHashWithCreation?.creation || mintBidWithCreation?.creation;

  const { currentAccountAddress } = useWalletAddress();

  const { data: fullCreation } = useCreation({
    creationID: creation.creationID,
  });

  const askHash = askHashWithCreation?.askHash || mintBidWithCreation?.askHash;
  const { data: mintAsk } = useMintAsk({ askHash });

  const { data: allMintBids, isLoading: loadingAllMintBids } =
    useMintBidsByAskHash({
      askHash,
      config: {
        refetchOnMount: 'always',
      },
    });

  const isSeller = !!askHashWithCreation;
  const isBidder = !!mintBidWithCreation;

  const [isClaimModalOpen, setIsClaimModalOpen] = useToggle(false);

  const { convertETHToCurrency } = useETHPrice();

  const firstBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    return (
      allMintBids.sort(
        (a, b) => parseISO(a.bidAt).getTime() - parseISO(b.bidAt).getTime()
      )[0] || null
    );
  }, [allMintBids]);

  const highestBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    return (
      allMintBids.sort((a, b) => Number(b.price) - Number(a.price))[0] || null
    );
  }, [allMintBids]);

  const myLatestBid = useMemo(() => {
    if (loadingAllMintBids) {
      return undefined;
    }

    if (!currentAccountAddress) {
      return null;
    }

    return (
      allMintBids
        .filter(
          (mintBid) => mintBid.signer.toLowerCase() === currentAccountAddress
        )
        .sort(
          (a, b) => parseISO(b.bidAt).getTime() - parseISO(a.bidAt).getTime()
        )[0] || null
    );
  }, [allMintBids, currentAccountAddress]);

  const auctionDeadLine = useMemo(() => {
    if (mintAsk) {
      const deadline = parseISO(mintAsk.endTime)
      if (deadline instanceof Date && !isNaN(deadline.getTime())) {
        // check for Invalid Date
        return deadline;
      }
    }

    return null;
  }, [mintAsk]);

  const isDeadlineExceeded = auctionDeadLine && isPast(auctionDeadLine);

  const getFormattedBidPrice = (
    bid: undefined | MintBidWithUser | null
  ): string => {
    if (typeof bid === 'undefined') {
      return t('loading');
    }

    if (!bid) {
      return t('auctionActivity.activityCard.noBidsYet');
    }

    return `${formatEther(bid.price)} ETH`;
  };

  const isFinished = mintAsk?.status === MintAskStatus.Finished;
  const isCancelled = mintAsk?.status === MintAskStatus.Cancelled;

  // For formatting countdown
  const renderer = ({ hours, minutes, seconds }) => (
    <span>
      {zeroPad(hours)}:{zeroPad(minutes)}:{zeroPad(seconds)}
    </span>
  );

  return (
    <div className={styles.auctionActivityCard}>
      <div className={styles.cardHeading}>
        <div className={styles.creationThumbnail}>
          {fullCreation ? (
            <CreationThumbnail creation={fullCreation} size={150} />
          ) : (
            <img
              src={convertImgixURL(creation.imageURL, {
                w: 140,
                h: 140,
                fit: 'crop',
              })}
              alt={creation.creationTitle}
            />
          )}
        </div>

        <div className={styles.auctionInfo}>
          <div>
            <div className={styles.currentBid}>
              {isSeller
                ? t('auctionActivity.activityCard.highestBid')
                : t('auctionActivity.activityCard.yourBid')}
              :
            </div>
            <div className={styles.currentBidPrice}>
              {isSeller ? getFormattedBidPrice(highestBid) : null}
              {isBidder ? (
                <>
                  {getFormattedBidPrice(myLatestBid)}
                  {typeof myLatestBid !== 'undefined' ? (
                    <AuctionStatusIcon
                      isWinning={
                        myLatestBid && myLatestBid.bidID === highestBid.bidID
                      }
                    />
                  ) : null}
                </>
              ) : null}
            </div>

            {/* Show price in USD */}
            {isSeller && highestBid ? (
              <div className={styles.currentBidPriceSubCurrency}>
                {convertETHToCurrency(highestBid.price, 'USD')} USD
              </div>
            ) : null}
            {isBidder && myLatestBid ? (
              <div className={styles.currentBidPriceSubCurrency}>
                {convertETHToCurrency(myLatestBid.price, 'USD')} USD
              </div>
            ) : null}
          </div>

          {isSeller ? (
            <CustomButton
              onClick={() => setIsClaimModalOpen(true)}
              disabled={
                !highestBid || mintAsk?.status !== MintAskStatus.Ongoing
              }
            >
              {t('auctionActivity.activityCard.claim')}
            </CustomButton>
          ) : null}

          {isBidder &&
            fullCreation &&
            mintAsk &&
            mintAsk.status !== MintAskStatus.Cancelled ? (
            <BidStatus
              creation={fullCreation}
              highestBid={highestBid}
              auctionFinished={mintAsk.status === MintAskStatus.Finished}
              isDeadlineExceeded={isDeadlineExceeded}
            />
          ) : null}
        </div>
      </div>

      <div className={styles.creationDetail}>
        <div className={styles.creationTitle}>
          {(i18n.language === 'ja' && creation.creationTitleJA) ||
            creation.creationTitle}
        </div>
        <div className={styles.creationCreator}>
          <FramedIcon
            imageURL={creation.creatorProfileImageURL}
            size={25}
            certified
          />
          <div>
            {(i18n.language === 'ja' && creation.creatorNameJA) ||
              creation.creatorName}
          </div>
        </div>
      </div>

      <div className={styles.auctionDetail}>
        <div>
          {isSeller ? (
            <>
              <h3>{t('auctionActivity.activityCard.reservePrice')}</h3>
              <div>{formatEther(askHashWithCreation.reservePrice)} ETH</div>
            </>
          ) : null}

          {isBidder ? (
            <>
              <h3>{t('auctionActivity.activityCard.highestBid')}</h3>
              <div>{getFormattedBidPrice(highestBid)}</div>
            </>
          ) : null}
        </div>

        <div>
          <h3>{t('auctionActivity.activityCard.timeRemaining')}</h3>
          <div
            className={classNames({
              [styles.textSubtle]: isDeadlineExceeded,
              [styles.textRed]:
                !isDeadlineExceeded &&
                auctionDeadLine &&
                differenceInHours(auctionDeadLine, new Date()) < 1,
            })}
          >
            {!isFinished && !isCancelled ? (
              <>
                {auctionDeadLine ? (
                  <Countdown date={auctionDeadLine} renderer={renderer} />
                ) : (
                  t('auctionActivity.activityCard.notStartedYet')
                )}
              </>
            ) : null}

            {isFinished ? t('auctionActivity.activityCard.finished') : null}

            {isCancelled ? t('auctionActivity.activityCard.cancelled') : null}
          </div>
        </div>
      </div>

      <Link href={routes.creationDetail(creation.creationID)}>
        <a className={styles.creationLink}>
          {t('auctionActivity.activityCard.viewArtwork')}
        </a>
      </Link>

      {askHashWithCreation && mintAsk && highestBid ? (
        <ClaimAuctionModal
          creation={fullCreation}
          isDeadlineExceeded={isDeadlineExceeded}
          mintAsk={mintAsk}
          bid={highestBid}
          bidderName={
            (i18n.language === 'ja' && highestBid.user.displayNameJA) ||
            highestBid.user.displayName
          }
          isOpen={isClaimModalOpen}
          onRequestClose={() => setIsClaimModalOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default AuctionActivityCard;
